import { Badge, Button, Card, Input, Section } from '@memorizecards/ui';
import type { ReactNode } from 'react';
import { useDeferredValue, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { IconPlay, IconSearch, IconStar, IconTarget } from '../components/Icons';
import { Carregando, Erro, TituloPagina, Vazio } from '../components/estado';
import { formatNumber } from '../lib/format';
import { useDecks } from '../lib/queries';
import type { DeckSummary } from '../lib/types';

export function Baralhos(): ReactNode {
  const { data: decks, isPending, error, refetch } = useDecks();
  const [busca, setBusca] = useState('');
  const [pacoteAberto, setPacoteAberto] = useState<string | null>(null);

  // A filtragem percorre até 220 matérias a cada tecla. `useDeferredValue`
  // mantém a escrita fluida sem precisar de debounce manual.
  const buscaAdiada = useDeferredValue(busca);

  const grupos = useMemo(() => agruparPorPacote(decks ?? [], buscaAdiada), [
    decks,
    buscaAdiada,
  ]);

  const totais = useMemo(() => {
    const lista = decks ?? [];
    return {
      cards: lista.reduce((s, d) => s + d.total, 0),
      revisar: lista.reduce((s, d) => s + d.revisar + d.aprendizado, 0),
      materias: lista.length,
    };
  }, [decks]);

  return (
    <Section spacing="md">
      <TituloPagina
        eyebrow="Seus estudos"
        titulo="Flashcards"
        descricao="Escolha a matéria e comece. Os cards vencidos aparecem primeiro; os novos entram quando não há nada para rever."
      />

      {isPending ? <Carregando /> : null}
      {error ? <Erro error={error} aoTentarNovamente={() => void refetch()} /> : null}

      {decks ? (
        <>
          <div className="mb-8 grid gap-4 sm:grid-cols-3">
            <ResumoCard rotulo="Matérias" valor={formatNumber(totais.materias)} />
            <ResumoCard rotulo="Cards disponíveis" valor={formatNumber(totais.cards)} />
            <ResumoCard
              rotulo="Para revisar hoje"
              valor={formatNumber(totais.revisar)}
              destaque={totais.revisar > 0}
            />
          </div>

          <div className="mb-8 max-w-xl">
            <Input
              label="Buscar matéria ou pacote"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder="Ex.: Direito Tributário"
              type="search"
              className="pl-11"
            />
            <IconSearch className="pointer-events-none -mt-[42px] ml-4 size-4 text-mc-ink3" />
          </div>

          {grupos.length === 0 ? (
            <Vazio
              titulo="Nada encontrado"
              descricao="Tente outro termo ou limpe a busca para ver todas as matérias."
              acao={
                <Button variant="ghost" size="sm" onClick={() => setBusca('')}>
                  Limpar busca
                </Button>
              }
            />
          ) : (
            <div className="flex flex-col gap-8">
              {grupos.map((grupo) => {
                // Com busca activa todos os pacotes ficam abertos: esconder o
                // resultado atrás de um clique anula a razão de ter buscado.
                const aberto =
                  buscaAdiada.trim().length > 0 ||
                  pacoteAberto === null ||
                  pacoteAberto === grupo.pacote;

                return (
                  <section key={grupo.pacote}>
                    <button
                      type="button"
                      onClick={() =>
                        setPacoteAberto((atual) =>
                          atual === grupo.pacote ? null : grupo.pacote,
                        )
                      }
                      aria-expanded={aberto}
                      className="mb-4 flex min-h-[44px] w-full items-center justify-between gap-4 border-b border-mc-line pb-3 text-left"
                    >
                      <h2 className="text-mc-card-title font-semibold text-mc-ink">
                        {grupo.pacote}
                      </h2>
                      <span className="mc-mono shrink-0">
                        {grupo.decks.length}{' '}
                        {grupo.decks.length === 1 ? 'matéria' : 'matérias'} ·{' '}
                        {formatNumber(grupo.cards)} cards
                      </span>
                    </button>

                    {aberto ? (
                      <ul className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                        {grupo.decks.map((deck) => (
                          <li key={deck.id}>
                            <DeckCard deck={deck} />
                          </li>
                        ))}
                      </ul>
                    ) : null}
                  </section>
                );
              })}
            </div>
          )}
        </>
      ) : null}
    </Section>
  );
}

function ResumoCard({
  rotulo,
  valor,
  destaque = false,
}: {
  rotulo: string;
  valor: string;
  destaque?: boolean;
}): ReactNode {
  return (
    <Card padding="md">
      <p className="mc-mono">{rotulo}</p>
      <p
        className={`mt-2 font-mc-display text-3xl font-semibold tabular-nums ${
          destaque ? 'text-mc-accent' : 'text-mc-ink'
        }`}
      >
        {valor}
      </p>
    </Card>
  );
}

function DeckCard({ deck }: { deck: DeckSummary }): ReactNode {
  const pendentes = deck.revisar + deck.aprendizado;

  return (
    <Card interactive padding="md" className="flex h-full flex-col gap-4">
      <div className="flex items-start justify-between gap-3">
        <h3 className="text-mc-card-title font-medium text-mc-ink">{deck.nome}</h3>
        {pendentes > 0 ? (
          <Badge tone="accent" className="shrink-0">
            {formatNumber(pendentes)}
          </Badge>
        ) : null}
      </div>

      <dl className="grid grid-cols-3 gap-2 text-center">
        <Metrica rotulo="Novos" valor={deck.novos} />
        <Metrica rotulo="Aprendendo" valor={deck.aprendizado} tom="warning" />
        <Metrica rotulo="Revisar" valor={deck.revisar} tom="accent" />
      </dl>

      <div className="mt-auto flex flex-wrap gap-2">
        <Link to={`/estudar?deck=${deck.id}`} className="flex-1">
          <Button size="sm" fullWidth>
            <IconPlay className="size-4" />
            Estudar
          </Button>
        </Link>
        <Link to={`/simulado?deck=${deck.id}`} aria-label={`Simulado de ${deck.nome}`}>
          <Button size="sm" variant="ghost">
            <IconTarget className="size-4" />
          </Button>
        </Link>
        {deck.favoritas > 0 ? (
          <Link
            to={`/favoritas?deck=${deck.id}`}
            aria-label={`Favoritas de ${deck.nome}`}
          >
            <Button size="sm" variant="ghost">
              <IconStar className="size-4" />
              {deck.favoritas}
            </Button>
          </Link>
        ) : null}
      </div>
    </Card>
  );
}

function Metrica({
  rotulo,
  valor,
  tom = 'neutro',
}: {
  rotulo: string;
  valor: number;
  tom?: 'neutro' | 'accent' | 'warning';
}): ReactNode {
  return (
    <div className="rounded-mc-field border border-mc-line bg-[rgba(0,0,0,0.2)] px-2 py-2">
      <dt className="mc-mono text-[0.6rem]">{rotulo}</dt>
      <dd
        className={`mt-0.5 text-sm font-semibold tabular-nums ${
          valor === 0
            ? 'text-mc-ink3'
            : tom === 'accent'
              ? 'text-mc-accent'
              : tom === 'warning'
                ? 'text-mc-warning'
                : 'text-mc-ink'
        }`}
      >
        {formatNumber(valor)}
      </dd>
    </div>
  );
}

interface Grupo {
  pacote: string;
  decks: DeckSummary[];
  cards: number;
}

function agruparPorPacote(decks: DeckSummary[], busca: string): Grupo[] {
  const termo = normalizar(busca);
  const filtrados = termo
    ? decks.filter(
        (d) =>
          normalizar(d.nome).includes(termo) || normalizar(d.pacote).includes(termo),
      )
    : decks;

  const mapa = new Map<string, Grupo>();
  for (const deck of filtrados) {
    const grupo = mapa.get(deck.pacote) ?? { pacote: deck.pacote, decks: [], cards: 0 };
    grupo.decks.push(deck);
    grupo.cards += deck.total;
    mapa.set(deck.pacote, grupo);
  }

  return [...mapa.values()].sort((a, b) => a.pacote.localeCompare(b.pacote, 'pt-BR'));
}

/** Sem acentos e em minúsculas: "tributario" tem de encontrar "Tributário". */
function normalizar(valor: string): string {
  return valor
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}
